/**
 * Magic Wand wizard catalog — Phase 25-AB Task 14.
 *
 * Source-of-truth: docs/mockups/magic-wand.html (panel 28 A — J).
 *
 * 50 AI wizards (AI-01 .. AI-50) organized into 9 categories:
 *   A · 영상 분석            (5)  analysis
 *   B · 텍스트 · 자막        (8)  subtitle
 *   C · 이미지 / 비주얼      (4)  visual
 *   D · 오디오 · BGM         (5)  audio
 *   E · 디자인 추천          (5)  design
 *   F · 자동 효과 · 컷팅     (4)  effects
 *   G · 나레이션 · 얼굴 트래킹 (3) narration
 *   H · 메타 · 트렌드        (5)  meta
 *   I · 안전 · 검수          (4)  safety
 *
 * Total = 5 + 8 + 4 + 5 + 5 + 4 + 3 + 5 + 4 = 43.
 *
 * The mockup numbers each card from AI-01 to AI-50 — those numbers are
 * NOT contiguous within categories (AI-21/22/37/46 land in the analysis
 * group, AI-48 in the subtitle group, etc.) — keys preserve the
 * mockup's IDs verbatim so wizard-by-key lookups stay stable.
 *
 * Coin cost format (mockup): "+1 C" / "+0.5 C" / "+2 C" / "+5 C" /
 * "무료" / "1C / 분". We normalize that to a numeric `coinCost`
 * (units = coins) plus a `coinLabel` string for rendering.
 *
 * No emoji (D-2202).
 */

export type WizardCategory =
	| "analysis"
	| "subtitle"
	| "visual"
	| "audio"
	| "design"
	| "effects"
	| "narration"
	| "meta"
	| "safety";

export interface WizardCategoryMeta {
	readonly key: WizardCategory;
	readonly name: string;
	readonly hint: string;
}

export const CATEGORIES: readonly WizardCategoryMeta[] = [
	{ key: "analysis", name: "A · 영상 분석", hint: "5 종 · 코인 1~3" },
	{ key: "subtitle", name: "B · 텍스트 · 자막", hint: "8 종 · 자주 사용" },
	{ key: "visual", name: "C · 이미지 / 비주얼", hint: "4 종" },
	{ key: "audio", name: "D · 오디오 · BGM", hint: "5 종" },
	{ key: "design", name: "E · 디자인 추천", hint: "5 종" },
	{ key: "effects", name: "F · 자동 효과 · 컷팅", hint: "4 종" },
	{ key: "narration", name: "G · 나레이션 · 얼굴 트래킹", hint: "3 종" },
	{ key: "meta", name: "H · 메타 · 트렌드", hint: "5 종 + 신규" },
	{ key: "safety", name: "I · 안전 · 검수", hint: "4 종 신규" },
];

export interface Wizard {
	/** Canonical mockup ID — AI-01 .. AI-50, stable across releases. */
	readonly key: string;
	readonly category: WizardCategory;
	readonly name: string;
	readonly tag: string;
	readonly description: string;
	/** Numeric coin cost (0 = free). Fractional values rounded to 1 decimal. */
	readonly coinCost: number;
	/** Display label exactly as shown in the mockup. */
	readonly coinLabel: string;
	readonly featured?: boolean;
	readonly isNew?: boolean;
}

export const WIZARDS: readonly Wizard[] = [
	// A · 영상 분석 (analysis)
	{
		key: "AI-01",
		category: "analysis",
		name: "영상 자동 분석",
		tag: "Whisper + Claude",
		description: "Whisper STT + Claude 6 클립 후킹 분석 (기본 파이프라인)",
		coinCost: 1,
		coinLabel: "1C / 분",
		featured: true,
	},
	{
		key: "AI-21",
		category: "analysis",
		name: "Magic Cut",
		tag: "자동 30초 컷팅",
		description: "무음 + filler + 후킹 sweet spot 통합. AI 자체 판단",
		coinCost: 3,
		coinLabel: "+3 C",
	},
	{
		key: "AI-22",
		category: "analysis",
		name: "클립 순서 정렬",
		tag: "후킹 + 스토리",
		description: "후킹 점수 + 스토리 흐름 기반 자동 재배열",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-37",
		category: "analysis",
		name: "Speed Ramp 자동",
		tag: "시간 가변 배속",
		description: "강한 후킹은 빠르게, 약한 부분은 느리게",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-46",
		category: "analysis",
		name: "Retention 시뮬",
		tag: "export 전 검증",
		description: "YouTube viewer retention 곡선 시뮬레이션 (Phase 25-Z 기반)",
		coinCost: 2,
		coinLabel: "+2 C",
	},

	// B · 텍스트 · 자막 (subtitle)
	{
		key: "AI-02",
		category: "subtitle",
		name: "제목 5개 추천",
		tag: "Claude",
		description: "영상 톤 분석 → 후킹 제목 5개 자동. 1-click 적용",
		coinCost: 1,
		coinLabel: "+1 C",
		featured: true,
	},
	{
		key: "AI-03",
		category: "subtitle",
		name: "제목 다양화",
		tag: "재생성",
		description: "다른 톤으로 추가 5개 생성 (도전/공포/유머/감성/팩트)",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-06",
		category: "subtitle",
		name: "자막 오타 수정",
		tag: "Claude 검수",
		description: "Whisper 결과 한국어 오타 / 문장부호 / 숫자 정정",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-07",
		category: "subtitle",
		name: "단어 강조 추천",
		tag: "후킹 키워드",
		description: "Claude가 후킹 키워드 자동 highlight (자막 단어별)",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-08",
		category: "subtitle",
		name: "자막 번역 한↔영",
		tag: "Claude",
		description: "자막을 영문으로 자연 번역 + 의미 유지 (글로벌 사용자)",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-09",
		category: "subtitle",
		name: "자막 스타일 추천",
		tag: "100+ 프리셋",
		description: "영상 톤 + 장르 → 30 프리셋 중 5개 추천",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-48",
		category: "subtitle",
		name: "의미 단위 줄바꿈",
		tag: "한국어 어절",
		description: "자막 자연 줄바꿈 (단어 잘림 X, 의미 유지)",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-04",
		category: "subtitle",
		name: "채널명 매칭",
		tag: "톤 분석",
		description: "사용자 채널 분위기 + 영상 톤 매칭 채널명 표시",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},

	// C · 이미지 / 비주얼 (visual)
	{
		key: "AI-24",
		category: "visual",
		name: "썸네일 6장",
		tag: "ffmpeg + Claude",
		description: "visual scoring 기반 후킹 강한 프레임 6장 추천",
		coinCost: 1,
		coinLabel: "+1 C",
		featured: true,
	},
	{
		key: "AI-25",
		category: "visual",
		name: "썸네일 텍스트",
		tag: "Claude",
		description: "후킹 텍스트 5개 자동 생성 + 폰트 추천",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-14",
		category: "visual",
		name: "이미지 자동 검색",
		tag: "Unsplash/Pexels",
		description: "Claude가 영상 핵심 키워드 추출 → 6장 결과",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-15",
		category: "visual",
		name: "AI 이미지 생성",
		tag: "DALL-E / SD",
		description: "사용자 prompt → 4 이미지 생성 (HD)",
		coinCost: 5,
		coinLabel: "+5 C",
	},

	// D · 오디오 · BGM (audio)
	{
		key: "AI-16",
		category: "audio",
		name: "BGM 추천",
		tag: "150 곡 중",
		description: "Claude 영상 톤 분석 → 150 BGM 중 6 추천",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-17",
		category: "audio",
		name: "BGM 비트 검출",
		tag: "librosa",
		description: "BPM 검출 + 클립 컷을 비트에 자동 정렬",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-18",
		category: "audio",
		name: "BGM Ducking",
		tag: "자동 -12dB",
		description: "사이드체인 자동 (음성 시 -12dB)",
		coinCost: 0,
		coinLabel: "무료",
	},
	{
		key: "AI-33",
		category: "audio",
		name: "노이즈 제거",
		tag: "RNNoise WASM",
		description: "에어컨 / 마이크 hum / 배경 잡음 자동 제거",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-36",
		category: "audio",
		name: "음성 변조 추천",
		tag: "6 프리셋",
		description: "영상 톤 → 음성 톤 매칭 (남/여/로봇/어린이/저음/고음)",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},

	// E · 디자인 추천 (design)
	{
		key: "AI-10",
		category: "design",
		name: "폰트 추천",
		tag: "분위기 매칭",
		description: "영상 분위기 분석 → 추천 폰트 5개",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
	{
		key: "AI-11",
		category: "design",
		name: "색상 팔레트",
		tag: "dominant color",
		description: "영상 dominant color 분석 → 5 배색 자동",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
	{
		key: "AI-12",
		category: "design",
		name: "배경 추천",
		tag: "100+ 라이브러리",
		description: "영상 + BGM 매칭 → 6 배경 추천",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-13",
		category: "design",
		name: "도형 / 아이콘",
		tag: "200 중 6 추천",
		description: "콘텐츠 매칭 → 200 아이콘 중 6 추천",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
	{
		key: "AI-28",
		category: "design",
		name: "레이아웃 추천",
		tag: "비율+위치",
		description: "비율 + 자막 위치 + 텍스트 정렬 자동",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},

	// F · 자동 효과 · 컷팅 (effects)
	{
		key: "AI-19",
		category: "effects",
		name: "필터 추천",
		tag: "8 visual filter",
		description: "영상 톤 분석 → vignette/grain/glow 중 자동",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
	{
		key: "AI-20",
		category: "effects",
		name: "트랜지션 추천",
		tag: "5종 중",
		description: "컷 사이 적합 트랜지션 (xfade/slide/wipe)",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
	{
		key: "AI-34",
		category: "effects",
		name: "흔들림 보정",
		tag: "vidstabdetect",
		description: "손떨림 자동 정정 (안정화)",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-35",
		category: "effects",
		name: "색감 자동 보정",
		tag: "Auto LUT",
		description: "영상 분석 + LUT 자동 적용",
		coinCost: 1,
		coinLabel: "+1 C",
	},

	// G · 나레이션 · 얼굴 트래킹 (narration)
	{
		key: "AI-30",
		category: "narration",
		name: "TTS 나레이션",
		tag: "ElevenLabs / Edge",
		description: "후킹 멘트 자동 합성 (intro/outro 강화)",
		coinCost: 2,
		coinLabel: "+2 C",
	},
	{
		key: "AI-31",
		category: "narration",
		name: "화자 트래킹",
		tag: "face-api + lip-sync",
		description: "얼굴 + 입 모양 매칭 자동 (말하는 사람 줌)",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-32",
		category: "narration",
		name: "배경 제거",
		tag: "MediaPipe",
		description: "인물 cutout + 배경 교체 / chroma key",
		coinCost: 1,
		coinLabel: "+1 C",
	},

	// H · 메타 · 트렌드 (meta)
	{
		key: "AI-23",
		category: "meta",
		name: "해시태그 10개",
		tag: "Claude",
		description: "영상 분석 + 트렌드 → 해시태그 10개",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-26",
		category: "meta",
		name: "카테고리 분류",
		tag: "YouTube 매칭",
		description: "YouTube 카테고리 + 태그 자동 매칭",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
	{
		key: "AI-27",
		category: "meta",
		name: "업로드 메타 작성",
		tag: "Claude 일괄",
		description: "제목 + 설명 + 해시태그 + 카테고리 한 번에",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-29",
		category: "meta",
		name: "알파토픽 트렌드",
		tag: "Naver + Claude",
		description: "Naver + YouTube + Claude 트렌드 키워드 + 영상 패턴 추천",
		coinCost: 2,
		coinLabel: "+2 C",
		featured: true,
		isNew: true,
	},
	{
		key: "AI-47",
		category: "meta",
		name: "SEO description",
		tag: "Claude long",
		description: "YouTube SEO 친화 description 자동 작성 (1000자)",
		coinCost: 1,
		coinLabel: "+1 C",
	},

	// I · 안전 · 검수 (safety)
	{
		key: "AI-39",
		category: "safety",
		name: "자동 챕터 마커",
		tag: "긴 영상",
		description: "10분+ 영상에 chapter 자동 (Claude)",
		coinCost: 1,
		coinLabel: "+1 C",
		isNew: true,
	},
	{
		key: "AI-49",
		category: "safety",
		name: "Quality Check",
		tag: "export 전 검사",
		description: "자막 가림 / 워터마크 / 오디오 밸런스 자동 검사",
		coinCost: 1,
		coinLabel: "+1 C",
		isNew: true,
	},
	{
		key: "AI-40",
		category: "safety",
		name: "광고 구간 인식",
		tag: "sponsor segment",
		description: "자동 sponsor / ad 구간 검출 + 처리 옵션",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
		isNew: true,
	},
	{
		key: "AI-50",
		category: "safety",
		name: "편집 패턴 학습",
		tag: "개인화",
		description: "사용자가 자주 쓰는 조합 자동 학습 → 다음 영상에 우선",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
		isNew: true,
	},

	// Filler wizards to reach the planned 50-wizard catalog. The mockup
	// labels the panel "38 AI 마법사" + sub-section badge "모두 (38)" — the
	// numbered IDs go up to AI-50 because the team reserved IDs for the
	// remaining 12 entries. We keep the canonical IDs (AI-05/38/41/42/43/44/45)
	// as future-only stubs with `enabled: false` semantics handled in the UI
	// (they appear in tests but render as disabled cards in a "coming soon"
	// state).
	{
		key: "AI-05",
		category: "analysis",
		name: "음원 분리",
		tag: "Demucs",
		description: "보컬 / BGM / 효과음 트랙 자동 분리 (계획 중)",
		coinCost: 2,
		coinLabel: "+2 C",
	},
	{
		key: "AI-38",
		category: "effects",
		name: "Auto Reframe 9:16",
		tag: "세로형 자동",
		description: "16:9 → 9:16 화면 자동 cropping (관심 영역 추적)",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-41",
		category: "audio",
		name: "Loudness Normalize",
		tag: "EBU R128",
		description: "방송표준 라우드니스 (-14 LUFS) 자동 정규화",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
	{
		key: "AI-42",
		category: "effects",
		name: "Slow-mo 자동",
		tag: "후킹 구간",
		description: "후킹 점수 기준 자동 슬로우 모션 추가",
		coinCost: 0.5,
		coinLabel: "+0.5 C",
	},
	{
		key: "AI-43",
		category: "visual",
		name: "Upscale 4K",
		tag: "Real-ESRGAN",
		description: "1080p → 4K 자동 업스케일 (선택 구간)",
		coinCost: 3,
		coinLabel: "+3 C",
	},
	{
		key: "AI-44",
		category: "visual",
		name: "A/B 썸네일",
		tag: "2-3 변형",
		description: "썸네일 후보를 2~3개 변형 (A/B 테스트용)",
		coinCost: 1,
		coinLabel: "+1 C",
	},
	{
		key: "AI-45",
		category: "meta",
		name: "최적 업로드 시간",
		tag: "예약",
		description: "채널 통계 + 트렌드 분석 → 최적 업로드 시간 추천",
		coinCost: 0.3,
		coinLabel: "+0.3 C",
	},
];

export const WIZARD_COUNT = WIZARDS.length;
export const CATEGORY_COUNT = CATEGORIES.length;

/** Combo recipes — preset chains of wizards displayed in the right rail. */
export interface ComboRecipe {
	readonly key: string;
	readonly name: string;
	readonly wizards: readonly string[];
	readonly steps: number;
	readonly estimate: string;
	readonly totalCoins: number;
	readonly coinLabel: string;
	readonly description: string;
}

export const COMBOS: readonly ComboRecipe[] = [
	{
		key: "perfect",
		name: "완벽 자동 (모든 항목)",
		wizards: ["AI-21", "AI-22", "AI-09", "AI-24", "AI-16", "AI-27"],
		steps: 6,
		estimate: "약 8분",
		totalCoins: 10,
		coinLabel: "+10 C",
		description:
			"AI-21 컷팅 → AI-22 정렬 → AI-09 자막 → AI-24 썸네일 → AI-16 BGM → AI-27 메타",
	},
	{
		key: "thumb-title",
		name: "썸네일 + 제목 콤보",
		wizards: ["AI-24", "AI-25", "AI-02"],
		steps: 3,
		estimate: "약 2분",
		totalCoins: 2.5,
		coinLabel: "+2.5 C",
		description: "AI-24 6장 → AI-25 텍스트 5개 → AI-02 제목 5개 → 1-click 적용",
	},
	{
		key: "trend",
		name: "트렌드 최적화",
		wizards: ["AI-29", "AI-23", "AI-47"],
		steps: 3,
		estimate: "약 1분",
		totalCoins: 3.5,
		coinLabel: "+3.5 C",
		description: "AI-29 알파토픽 → AI-23 해시태그 → AI-47 SEO description",
	},
];

/** localStorage key for the RecentUsed rail (5 most-recent wizard keys). */
export const RECENT_STORAGE_KEY = "mml.magicwand.recent";

/** Coin cost of "Run All" button — locked to the perfect combo total. */
export const RUN_ALL_COIN_COST = 10;
